import React, { Fragment, useEffect, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Divider, Paper, Stack, Table, TableBody,
  IconButton, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';
import EventIcon from '@mui/icons-material/Event';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import iziToast from 'izitoast';
import { useNavigate, useParams } from 'react-router-dom';

import JournalLayout from '../../components/Journal/JournalLayout';
import StudentHistoryTooltip, { StudentHistoryInline } from '../../components/Journal/StudentHistoryTooltip';
import useIsMobile from '../../utils/useIsMobile';
import { getGroup } from '../../actions/journal';
import { PERMISSIONS, useCurrentUser } from '../../utils/currentUser';
import { internalServerError, journalSheetLink } from '../../utils/consts/pathRoutes';
import { formatDate, studentCardLink, studentName } from '../../utils/journalFormat';
import AddStudentModal from './AddStudentModal';
import GroupModal from './GroupModal';
import MembershipPeriodModal from './MembershipPeriodModal';
import TeacherModal from './TeacherModal';
import MoveStudentModal from './MoveStudentModal';

/**
 * Подпись преподавателя на карточке группы.
 *
 * Завершённые связи с карточки не убираются — кто вёл группу, это история. Но одной
 * блёклости мало: «Иванова · куратор» и «Пелымская · куратор» подряд читаются как два
 * действующих куратора, хотя база такого не допускает. Поэтому у закрытой связи в
 * подписи стоит дата, до которой человек вёл группу.
 */
const teacherLabel = (teacher) => [
  teacher.name,
  teacher.is_curator ? 'куратор' : null,
  teacher.is_hourly ? 'почасовик' : null,
  teacher.is_active ? null : `до ${formatDate(teacher.date_to)}`,
].filter(Boolean).join(' · ');

// Краткая справка по слушателю. Набор полей тот же, что видит старая роль
// «Преподаватель» на главной: у преподавателя ЭЖ доступа к карточке контингента нет,
// а телефон и почту он спрашивает каждый день.
const INFO_FIELDS = [
  ['ФИО (рус.)', (student) => student.russian_name],
  ['ФИО (лат.)', (student) => student.latin_name],
  ['Пол', (student) => student.gender],
  ['Страна', (student) => student.country],
  ['Тип обучения', (student) => student.education_type],
  ['Форма обучения', (student) => student.form_study],
  ['Количество часов', (student) => student.hours_number],
  ['Регистрация в системе', (student) => formatDate(student.registered_in_system)],
  ['Почта', (student) => student.first_student_email],
  ['Вторая почта', (student) => student.second_student_email],
  ['Телефон', (student) => student.contact_phone_number],
];

const StudentInfo = ({ student }) => (
  <Box sx={{ py: 1, pl: 2 }}>
    <Stack direction="row" sx={{ flexWrap: 'wrap', gap: '4px 32px' }}>
      {INFO_FIELDS.map(([label, read]) => (
        <Box key={label} sx={{ minWidth: 190 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {label}
          </Typography>
          <Typography variant="body2">{read(student) || '—'}</Typography>
        </Box>
      ))}
    </Stack>
  </Box>
);

/** Карточка группы: преподаватели, куратор и состав с историей выбытий. */
const GroupPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useCurrentUser();
  const canManage = can(PERMISSIONS.JOURNAL_MANAGE);
  // Преподаватель ЭЖ карточку контингента не открывает, поэтому для него щелчок
  // по строке раскрывает справку, а не ведёт на страницу, куда его не пустят.
  const canOpenCard = can(PERMISSIONS.STUDENTS_READ);
  const isMobile = useIsMobile();

  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [moving, setMoving] = useState(null); // { membership, mode: 'transfer' | 'expel' }
  const [period, setPeriod] = useState(null);
  const [expanded, setExpanded] = useState({}); // membership_id → раскрыта ли справка
  const [teacher, setTeacher] = useState(null); // { mode: 'create' } | { mode: 'edit', teacher }

  const toggleInfo = (student) => setExpanded((prev) => ({
    ...prev,
    [student.membership_id]: !prev[student.membership_id],
  }));

  const load = () => {
    setLoading(true);
    getGroup(id)
      .then(setGroup)
      .catch((e) => iziToast.error({ message: internalServerError(e), position: 'topRight' }))
      .finally(() => setLoading(false));
  };

  useEffect(load, [id]);

  if (loading) {
    return (
      <JournalLayout activeTab="group">
        <Stack alignItems="center" sx={{ py: 8 }}><CircularProgress color="warning" /></Stack>
      </JournalLayout>
    );
  }

  if (!group) return <JournalLayout activeTab="group" />;

  const active = group.students.filter((student) => student.is_active);
  const former = group.students.filter((student) => !student.is_active);

  // На телефоне строки с признаками и кнопками переносятся, а не уезжают за край.
  const wrapOnMobile = isMobile ? { spacing: 0, sx: { flexWrap: 'wrap', gap: 1 } } : {};

  const openStudent = (student) => (canOpenCard ? navigate(studentCardLink(student)) : toggleInfo(student));

  /**
   * Слушатель на телефоне — карточкой. Всё, что в таблице прячется в подсказки
   * (история перемещений, подписи кнопок), здесь выведено текстом.
   */
  const renderStudentCard = (student) => {
    const isOpen = expanded[student.membership_id];

    return (
      <Paper
        key={student.membership_id} variant="outlined"
        className="journal-card journal-card--clickable"
        sx={{ opacity: student.is_active ? 1 : 0.55 }}
        onClick={() => openStudent(student)}
      >
        <div className="journal-card__head">
          <div>
            <div className="journal-card__title">{studentName(student)}</div>
            <div className="journal-card__meta">
              {[student.country, student.language_level && `уровень ${student.language_level}`]
                .filter(Boolean).join(' · ') || '—'}
            </div>
          </div>
          <IconButton
            size="small" aria-label="Краткая справка о слушателе"
            onClick={(event) => { event.stopPropagation(); toggleInfo(student); }}
          >
            {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </div>

        <div className="journal-card__row">
          {student.is_active ? (
            <span className="journal-muted">В группе с {formatDate(student.date_from)}</span>
          ) : (
            <Fragment>
              <Chip size="small" label={student.status} />
              <span className="journal-muted">
                {formatDate(student.date_from)} — {formatDate(student.date_to)}
              </span>
            </Fragment>
          )}
          {student.is_active && student.hours_carried_over > 0 && (
            <span className="journal-muted">· принесено {student.hours_carried_over} ч</span>
          )}
        </div>

        {student.comment && (
          <div className="journal-card__meta" style={{ whiteSpace: 'pre-wrap' }}>{student.comment}</div>
        )}

        {isOpen && (
          <Box
            sx={{ mt: 1, mx: -1.5, px: 1.5, py: 1, bgcolor: '#fafafa', cursor: 'default' }}
            onClick={(event) => event.stopPropagation()}
          >
            <Box sx={{ ml: -2 }}><StudentInfo student={student} /></Box>
            <Box sx={{ mt: 1 }}><StudentHistoryInline studentId={student.student_id} /></Box>
          </Box>
        )}

        {canManage && (
          <div className="journal-card__actions" onClick={(event) => event.stopPropagation()}>
            <Button size="small" startIcon={<EventIcon />} onClick={() => setPeriod(student)}>
              Даты
            </Button>
            {student.is_active && (
              <Fragment>
                <Button
                  size="small" startIcon={<SwapHorizIcon />}
                  onClick={() => setMoving({ membership: student, mode: 'transfer' })}
                >
                  Перевод
                </Button>
                <Button
                  size="small" color="error" startIcon={<PersonRemoveIcon />}
                  onClick={() => setMoving({ membership: student, mode: 'expel' })}
                >
                  Отчислить
                </Button>
              </Fragment>
            )}
          </div>
        )}
      </Paper>
    );
  };

  const renderStudentCards = () => (
    <div className="journal-cards">
      {active.length === 0 && (
        <Paper variant="outlined" className="journal-card journal-muted" sx={{ textAlign: 'center' }}>
          В группе пока никого нет
        </Paper>
      )}

      {active.map(renderStudentCard)}

      {former.length > 0 && (
        <Divider textAlign="left" sx={{ my: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Выбывшие — {former.length}
          </Typography>
        </Divider>
      )}

      {former.map(renderStudentCard)}
    </div>
  );

  return (
    <JournalLayout activeTab="group">
      <Box>
        <Stack
          direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}
          {...(isMobile && { direction: 'column', alignItems: 'flex-start', spacing: 1.5 })}
        >
          <Stack direction="row" alignItems="center" spacing={2} {...wrapOnMobile}>
            <h2 className="journal-title">{group.name}</h2>
            <Chip size="small" label={group.status} />
            <Typography variant="body2" color="text.secondary">
              {group.semester} семестр · {group.academic_year}
            </Typography>
            {group.is_distant && <Chip size="small" variant="outlined" label="дистант" />}
            {group.is_paid && <Chip size="small" variant="outlined" label="платная" />}
          </Stack>

          <Stack direction="row" spacing={1} {...wrapOnMobile}>
            {canManage && (
              <Button startIcon={<EditIcon />} onClick={() => setEditOpen(true)}>
                Изменить группу
              </Button>
            )}
            {canManage && (
              <Button startIcon={<PersonAddIcon />} onClick={() => setAddOpen(true)}>
                Добавить слушателя
              </Button>
            )}
            <Button
              variant="contained" startIcon={<MenuBookIcon />}
              onClick={() => navigate(journalSheetLink(group.id))}
            >
              Журнал
            </Button>
          </Stack>
        </Stack>

        {/* Комментарий — единственное место, где написано, чем эта группа отличается:
            например, что она набрана в Даляне. Без него на карточке одни номера. */}
        {(group.comment || group.opened_at) && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
            {group.opened_at && `Открыта ${formatDate(group.opened_at)}`}
            {group.opened_at && group.comment && ' · '}
            {group.comment}
          </Typography>
        )}

        <Paper sx={{ p: 2, mb: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <p className="journal-subtitle" style={{ margin: 0 }}>Преподаватели</p>
            {canManage && (
              <Button size="small" startIcon={<PersonAddIcon />} onClick={() => setTeacher({ mode: 'create' })}>
                Назначить
              </Button>
            )}
          </Stack>

          {group.teachers.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Преподаватели не назначены.
              {canManage && ' Нажмите «Назначить», чтобы добавить преподавателя или куратора.'}
            </Typography>
          ) : (
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              {group.teachers.map((teacher) => (
                <Tooltip
                  key={teacher.id}
                  title={teacher.is_active
                    ? `Ведёт группу с ${formatDate(teacher.date_from)}${canManage ? ' · нажмите, чтобы изменить' : ''}`
                    : `Вёл группу с ${formatDate(teacher.date_from)} по ${formatDate(teacher.date_to)}`}
                >
                  <Chip
                    label={teacherLabel(teacher)}
                    variant={teacher.is_active ? 'filled' : 'outlined'}
                    onClick={canManage ? () => setTeacher({ mode: 'edit', teacher }) : undefined}
                    sx={{
                      borderLeft: teacher.color ? `4px solid ${teacher.color}` : undefined,
                      opacity: teacher.is_active ? 1 : 0.5,
                    }}
                  />
                </Tooltip>
              ))}
            </Stack>
          )}
        </Paper>

        <p className="journal-subtitle">
          Состав: {active.length} из {group.capacity}
        </p>

        {isMobile ? renderStudentCards() : (
          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40 }} />
                  <TableCell>ФИО</TableCell>
                  <TableCell>Страна</TableCell>
                  <TableCell>Уровень</TableCell>
                  <TableCell>В группе с</TableCell>
                  <TableCell align="center">Принесено часов</TableCell>
                  <TableCell>Комментарий</TableCell>
                  {canManage && <TableCell align="center">Действия</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {active.map((student) => (
                  <Fragment key={student.membership_id}>
                  <TableRow
                    hover sx={{ cursor: 'pointer' }}
                    onClick={() => (canOpenCard ? navigate(studentCardLink(student)) : toggleInfo(student))}
                  >
                    <TableCell onClick={(event) => event.stopPropagation()}>
                      <Tooltip title="Краткая справка о слушателе">
                        <IconButton size="small" onClick={() => toggleInfo(student)}>
                          {expanded[student.membership_id]
                            ? <ExpandLessIcon fontSize="small" />
                            : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <StudentHistoryTooltip studentId={student.student_id}>
                        <b>{studentName(student)}</b>
                      </StudentHistoryTooltip>
                    </TableCell>
                    <TableCell>{student.country || '—'}</TableCell>
                    <TableCell>{student.language_level || '—'}</TableCell>
                    <TableCell>{formatDate(student.date_from)}</TableCell>
                    <TableCell align="center">{student.hours_carried_over || '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 260, whiteSpace: 'pre-wrap' }}>
                      {student.comment || '—'}
                    </TableCell>
                    {canManage && (
                      <TableCell align="center" onClick={(event) => event.stopPropagation()}>
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="Изменить даты обучения в группе">
                            <IconButton size="small" onClick={() => setPeriod(student)}>
                              <EventIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Перевести в другую группу">
                            <Button
                              size="small" startIcon={<SwapHorizIcon />}
                              onClick={() => setMoving({ membership: student, mode: 'transfer' })}
                            >
                              Перевод
                            </Button>
                          </Tooltip>
                          <Tooltip title="Отчислить">
                            <Button
                              size="small" color="error" startIcon={<PersonRemoveIcon />}
                              onClick={() => setMoving({ membership: student, mode: 'expel' })}
                            >
                              Отчислить
                            </Button>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    )}
                  </TableRow>

                  {expanded[student.membership_id] && (
                    <TableRow>
                      <TableCell colSpan={canManage ? 8 : 7} sx={{ bgcolor: '#fafafa', py: 0 }}>
                        <StudentInfo student={student} />
                      </TableCell>
                    </TableRow>
                  )}
                  </Fragment>
                ))}

                {former.length > 0 && (
                  <Fragment>
                    <TableRow>
                      <TableCell colSpan={canManage ? 8 : 7} sx={{ bgcolor: '#fafafa' }}>
                        <Divider textAlign="left">
                          <Typography variant="caption" color="text.secondary">
                            Выбывшие — {former.length}
                          </Typography>
                        </Divider>
                      </TableCell>
                    </TableRow>

                    {former.map((student) => (
                      <Fragment key={student.membership_id}>
                      <TableRow
                        hover
                        sx={{ opacity: 0.55, cursor: 'pointer' }}
                        onClick={() => (canOpenCard ? navigate(studentCardLink(student)) : toggleInfo(student))}
                      >
                        <TableCell onClick={(event) => event.stopPropagation()}>
                          <IconButton size="small" onClick={() => toggleInfo(student)}>
                            {expanded[student.membership_id]
                              ? <ExpandLessIcon fontSize="small" />
                              : <ExpandMoreIcon fontSize="small" />}
                          </IconButton>
                        </TableCell>
                        <TableCell>{studentName(student)}</TableCell>
                        <TableCell>{student.country || '—'}</TableCell>
                        <TableCell>{student.language_level || '—'}</TableCell>
                        <TableCell>{formatDate(student.date_from)} — {formatDate(student.date_to)}</TableCell>
                        <TableCell align="center">
                          <Chip size="small" label={student.status} />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 260, whiteSpace: 'pre-wrap' }}>
                          {student.comment || '—'}
                        </TableCell>
                        {canManage && (
                          <TableCell align="center" onClick={(event) => event.stopPropagation()}>
                            <Tooltip title="Изменить даты обучения в группе">
                              <IconButton size="small" onClick={() => setPeriod(student)}>
                                <EventIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>

                      {expanded[student.membership_id] && (
                        <TableRow>
                          <TableCell colSpan={canManage ? 8 : 7} sx={{ bgcolor: '#fafafa', py: 0 }}>
                            <StudentInfo student={student} />
                          </TableCell>
                        </TableRow>
                      )}
                      </Fragment>
                    ))}
                  </Fragment>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {canManage && (
        <Fragment>
          <GroupModal
            open={editOpen} group={group}
            onClose={() => setEditOpen(false)}
            onSaved={() => { setEditOpen(false); load(); }}
          />
          <AddStudentModal
            open={addOpen} groupId={group.id}
            onClose={() => setAddOpen(false)}
            onAdded={() => { setAddOpen(false); load(); }}
          />
          <TeacherModal
            state={teacher} groupId={group.id}
            onClose={() => setTeacher(null)}
            onSaved={() => { setTeacher(null); load(); }}
          />
          <MembershipPeriodModal
            membership={period}
            onClose={() => setPeriod(null)}
            onSaved={() => { setPeriod(null); load(); }}
          />
          <MoveStudentModal
            state={moving} currentGroupId={group.id}
            onClose={() => setMoving(null)}
            onDone={() => { setMoving(null); load(); }}
          />
        </Fragment>
      )}
    </JournalLayout>
  );
};

export default GroupPage;
